<script setup lang="ts">
/**
 * `/system/monitoring` — Traces | Statistics | Settings.
 *
 * Each tab is gated on the permission its endpoints actually check, not on the family's `:read`.
 * The view this replaces got that wrong: `/system/egresses` required `egresses:read` while calling
 * an endpoint that checks `egresses:statistics`, so a statistics-only role could not open the view
 * it was created for, and a read-only role opened it and got 403s from every request.
 */
import { computed, inject, provide, ref, watchEffect } from 'vue'
import { useContext, useLocalization } from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import { useResourcesStore } from '@/stores/resources/resources-store'
import { openTraceIdKey, tracesViewContextKey } from './traces-view-context'
import { TracesViewContextImpl } from './traces-view-context-impl'
import { statisticsViewContextKey } from './statistics-view-context'
import { StatisticsViewContextImpl } from './statistics-view-context-impl'
import { loggingViewContextKey } from './logging-view-context'
import { LoggingViewContextImpl } from './logging-view-context-impl'
import TracesTab from './TracesTab.vue'
import StatisticsTab from './StatisticsTab.vue'
import SettingsTab from './SettingsTab.vue'
import { TabPanel, TabPanelTabDefinition } from '@hantera/design-system'


type TabId = 'traces' | 'statistics' | 'settings'

const context = useContext()
const t = useLocalization('monitoring')
const {
  egresses: egressesService,
  ingresses: ingressesService,
  jobs: jobsService,
  traces: tracesService,
} = useBackend()

const registryStore = inject<{ get(key: string): Promise<any> }>('$portal_registryStore', {
  async get() {
    return undefined
  },
})

const can = (pattern: string) => context.authorizeAll([pattern])

const sections = {
  ingress: can('ingresses:statistics'),
  egress: can('egresses:statistics'),
  jobs: can('jobs:statistics'),
}

const permissions = {
  traces: can('traces:read'),
  statistics: sections.ingress || sections.egress || sections.jobs,
  // Reading the grid needs :read on either family; editing needs :write, which is enforced per row.
  settings: can('ingresses:read') || can('egresses:read'),
}


/**
 * One context per tab, built here and injected downwards.
 *
 * View contexts rather than Pinia stores: everything they hold — the date range, the open trace, the
 * grid's unsaved edits — belongs to this view. Two Monitoring views open side by side would fight
 * over a singleton, showing each other's edits and saving them from whichever tab pressed the
 * button.
 *
 * Each is built only when its permissions allow, so a context that cannot legally fetch anything
 * does not exist to be injected.
 */
// Held as well as provided, so a deep link can seed its filters before `TracesTab` mounts and
// fetches.
const tracesContext = permissions.traces ? new TracesViewContextImpl({ tracesService }) : null

if (tracesContext) {
  provide(tracesViewContextKey, tracesContext)
}

/**
 * The trace a link asked to open, which `TracesTab` adopts as its initial selection.
 *
 * Not part of the traces view context: that models what the list is showing, and this is a one-shot
 * instruction from the URL. Nor a filter — see {@link applyDeepLink}.
 */
const openTraceId = ref<string | undefined>(undefined)

provide(openTraceIdKey, openTraceId)

if (permissions.statistics) {
  provide(
    statisticsViewContextKey,
    new StatisticsViewContextImpl({ egressesService, ingressesService, jobsService }, sections),
  )
}

if (permissions.settings) {
  provide(
    loggingViewContextKey,
    new LoggingViewContextImpl(
      { egressesService, ingressesService, registryStore, resourcesStore: useResourcesStore() },
      { ingressWrite: can('ingresses:write'), egressWrite: can('egresses:write') },
    ),
  )
}

/**
 * Where a cross-link points this view.
 *
 * `tab` selects the tab. `traceId` **opens** a trace and `span` selects a node within it, while
 * `resource` **filters** the list — a distinction that matters:
 *
 * - A link naming one trace already knows the answer, so it opens it and leaves the list on its
 *   normal window. Filtering to the single row it came for would box the operator into it; the
 *   surrounding traces are the context that makes one trace interpretable.
 * - A link naming resources has no single trace to open, so filtering *is* the answer.
 *
 * `resource` carries several terms separated by spaces rather than repeating the key: a view's query
 * is a flat `Record<string, string>` in both view managers, so a repeated key would keep only its
 * last value. A space cannot occur in a resource path, which is why it is safe as the separator.
 */
function applyDeepLink(): TabId | null {
  const query = context.currentView.value?.query

  if (!query) {
    return null
  }

  if (tracesContext) {
    const resources = (query['resource'] ?? '').split(' ').filter((term) => term.length > 0)

    if (resources.length > 0) {
      tracesContext.filters.resources.splice(
        0,
        tracesContext.filters.resources.length,
        ...resources,
      )
    }

    // Recorded before `TracesTab` mounts. The span is tagged with its trace, so it survives the
    // load it races rather than being cleared by it.
    const traceId = query['traceId']

    if (traceId) {
      openTraceId.value = traceId
      tracesContext.selectSpan(traceId, query['span'])
    }

    // A deep link into a specific resource or trace is a lookup, not a browse: drop the window to
    // "all time" so the trace is found however old it is.
    if (resources.length > 0 || traceId) {
      tracesContext.setRange(null)
    }
  }

  const requested = query['tab']

  return requested === 'traces' || requested === 'statistics' || requested === 'settings'
    ? requested
    : null
}

/**
 * A requested tab the caller cannot open falls back to the first one they can, rather than showing
 * an empty panel. The link is a convenience; losing it is better than landing nowhere.
 */
function initialTab(): TabId {
  const available = tabs.value.map((tab) => tab.id as TabId)
  const requested = applyDeepLink()

  if (requested && available.includes(requested)) {
    return requested
  }

  return available.includes('statistics') ? 'statistics' : (available[0] ?? 'statistics')
}

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = t('viewTitle')
  }
})

const tabs = computed<TabPanelTabDefinition[]>(() => {
  const available: TabPanelTabDefinition[] = []

  if (permissions.statistics) {
    available.push({ id: 'statistics', label: t('tabs.statistics'), component: StatisticsTab })
  }
  if (permissions.traces) {
    available.push({ id: 'traces', label: t('tabs.traces'), component: TracesTab })
  }
  if (permissions.settings) {
    available.push({ id: 'settings', label: t('tabs.settings'), component: SettingsTab })
  }

  return available
})

const activeTab = ref<TabId>(initialTab())

</script>

<template>
  <div class="monitoring-view">
    <div v-if="tabs.length === 0" class="empty">{{ t('noPermission') }}</div>

    <template v-else>
      <TabPanel
        v-model="activeTab"
        :tabs="tabs"
        class="tab-panel"
      />
    </template>
  </div>
</template>

<style scoped>
.monitoring-view {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.tab-panel {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.content {
  min-height: 0;
  border-top: 1px solid hsl(var(--color-70));
  background-color: hsl(var(--color-100));
  overflow: hidden;
}

.empty {
  padding: 1rem;
  font-size: 13px;
  color: hsl(var(--color-40));
}
</style>
